// frontend/src/pages/Templates.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FaLayerGroup, FaSave, FaTrash, FaEdit, FaCopy, FaPlus, FaTimes } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const Templates = () => {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        department: '',
        priority: 'Medium',
        frequency: 'One Time'
    });

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            const response = await axios.get('/api/templates');
            setTemplates(response.data.templates || []);
        } catch (error) {
            toast.error('Failed to load templates');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingTemplate) {
                await axios.put(`/api/templates/${editingTemplate.id}`, formData);
                toast.success('Template updated successfully');
            } else {
                await axios.post('/api/templates', formData);
                toast.success('Template saved successfully');
            }
            setShowModal(false);
            setEditingTemplate(null);
            setFormData({ title: '', description: '', department: '', priority: 'Medium', frequency: 'One Time' });
            fetchTemplates();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (template) => {
        if (window.confirm(`Delete template "${template.title}"?`)) {
            try {
                await axios.delete(`/api/templates/${template.id}`);
                toast.success('Template deleted');
                fetchTemplates();
            } catch (error) {
                toast.error('Failed to delete template');
            }
        }
    };

    const useTemplate = (template) => {
        // Store in localStorage to use in create task
        localStorage.setItem('selectedTemplate', JSON.stringify(template));
        window.location.href = '/create-task';
        toast.success('Template loaded! Go to Create Task');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="loader"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Task Templates</h1>
                    <p className="text-gray-500 mt-1">Save and reuse task templates</p>
                </div>
                <button
                    onClick={() => {
                        setEditingTemplate(null);
                        setFormData({ title: '', description: '', department: '', priority: 'Medium', frequency: 'One Time' });
                        setShowModal(true);
                    }}
                    className="btn-gradient px-4 py-2 rounded-lg text-white flex items-center gap-2"
                >
                    <FaPlus /> New Template
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map(template => (
                    <div key={template.id} className="glass-card p-6 hover:shadow-lg transition-all">
                        <div className="flex justify-between items-start mb-3">
                            <h3 className="font-semibold text-gray-800 text-lg">{template.title}</h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        setEditingTemplate(template);
                                        setFormData({
                                            title: template.title,
                                            description: template.description,
                                            department: template.department || '',
                                            priority: template.priority,
                                            frequency: template.frequency
                                        });
                                        setShowModal(true);
                                    }}
                                    className="text-blue-500 hover:text-blue-700"
                                >
                                    <FaEdit />
                                </button>
                                <button
                                    onClick={() => handleDelete(template)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{template.description}</p>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {template.department && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">🏢 {template.department}</span>
                            )}
                            <span className={`px-2 py-1 rounded text-xs ${
                                template.priority === 'High' ? 'priority-high' :
                                template.priority === 'Medium' ? 'priority-medium' : 'priority-low'
                            }`}>
                                {template.priority}
                            </span>
                            <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded text-xs">
                                {template.frequency}
                            </span>
                        </div>
                        <button
                            onClick={() => useTemplate(template)}
                            className="w-full btn-gradient py-2 rounded-lg text-white flex items-center justify-center gap-2"
                        >
                            <FaCopy /> Use Template
                        </button>
                    </div>
                ))}
            </div>

            {templates.length === 0 && (
                <div className="glass-card p-12 text-center">
                    <FaLayerGroup className="mx-auto text-5xl text-gray-300 mb-3" />
                    <p className="text-gray-500">No templates saved yet</p>
                    <p className="text-sm text-gray-400 mt-1">Create your first template to save time</p>
                </div>
            )}

            {/* Template Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowModal(false)}>
                    <div className="glass-card p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">
                                {editingTemplate ? 'Edit Template' : 'Save as Template'}
                            </h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Template Title *</label>
                                <input
                                    type="text"
                                    placeholder="e.g., Monthly Report Template"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    className="input-modern"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Description *</label>
                                <textarea
                                    placeholder="Task description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows="3"
                                    className="input-modern"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Department</label>
                                <input
                                    type="text"
                                    placeholder="Department"
                                    value={formData.department}
                                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                                    className="input-modern"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Priority</label>
                                    <select
                                        value={formData.priority}
                                        onChange={(e) => setFormData({...formData, priority: e.target.value})}
                                        className="input-modern"
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Frequency</label>
                                    <select
                                        value={formData.frequency}
                                        onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                                        className="input-modern"
                                    >
                                        <option value="One Time">One Time</option>
                                        <option value="Daily">Daily</option>
                                        <option value="Weekly">Weekly</option>
                                        <option value="Monthly">Monthly</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="submit" className="btn-gradient flex-1 py-2 rounded-lg text-white">
                                    <FaSave className="inline mr-2" /> {editingTemplate ? 'Update' : 'Save'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 bg-gray-300 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Templates;